import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Newspaper,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  User,
  Search,
  FileText,
  Image as ImageIcon,
  Save,
  X,
  ArrowLeft
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const NewsManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'General',
    image_url: '',
    attachments: [],
    status: 'published'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const categories = ['General', 'Events', 'Education', 'Research', 'Community', 'Announcements'];

  useEffect(() => {
    fetchNews();
  }, [statusFilter, categoryFilter]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      
      const response = await axios.get(`${backendUrl}/api/news`, { params });
      setNews(response.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch news articles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast({
        title: 'Missing fields',
        description: 'Title and content are required',
        variant: 'destructive'
      });
      return;
    }

    if (formData.content.length < 10) {
      toast({
        title: 'Content too short',
        description: 'Content must be at least 10 characters',
        variant: 'destructive'
      });
      return;
    }

    try {
      let imageUrl = formData.image_url;
      
      // Upload image if file is selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('file', imageFile);
        
        const uploadResponse = await axios.post(
          `${backendUrl}/api/admin/news/upload-image`,
          imageFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        imageUrl = uploadResponse.data.image_url;
      }
      
      const newsData = {
        ...formData,
        image_url: imageUrl || null,
        summary: formData.summary || null
      };
      
      if (editingNews) {
        // Update existing news
        await axios.put(`${backendUrl}/api/admin/news/${editingNews.id}`, newsData);
        toast({
          title: 'News Updated',
          description: 'News article has been updated successfully'
        });
      } else {
        // Create new news
        await axios.post(`${backendUrl}/api/admin/news`, newsData);
        toast({
          title: 'News Created',
          description: 'News article has been created successfully'
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchNews();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save news article',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      summary: newsItem.summary || '',
      content: newsItem.content,
      category: newsItem.category,
      image_url: newsItem.image_url || '',
      attachments: newsItem.attachments || [],
      status: newsItem.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (newsId, title) => {
    if (!window.confirm(`Are you sure you want to archive "${title}"? It can be restored later.`)) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/news/${newsId}`);
      toast({
        title: 'News Archived',
        description: 'News article has been archived'
      });
      fetchNews();
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete news article',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      content: '',
      category: 'General',
      image_url: '',
      attachments: [],
      status: 'published'
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingNews(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredNews = news.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="NEWS MANAGEMENT" />
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>

        {/* Header Actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4 flex-1 w-full md:w-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                <Plus size={18} className="mr-2" />
                Create News Article
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingNews ? 'Edit News Article' : 'Create New News Article'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter article title"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="summary">Summary</Label>
                  <Textarea
                    id="summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="Brief summary (optional)"
                    rows={2}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="content">Content *</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Full article content"
                    rows={8}
                    className="mt-2"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="image">Feature Image</Label>
                  <div className="mt-2 space-y-3">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="cursor-pointer"
                    />
                    {imagePreview && (
                      <div className="relative w-full h-48 border rounded-lg overflow-hidden">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview(null);
                          }}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    )}
                    {formData.image_url && !imagePreview && (
                      <div className="text-sm text-gray-600">
                        Current: {formData.image_url}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      resetForm();
                    }}
                  >
                    <X size={16} className="mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                    <Save size={16} className="mr-2" />
                    {editingNews ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Filter by Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Filter by Category</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button onClick={fetchNews} className="w-full bg-[#2c3e7d]">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* News List */}
        <Card>
          <CardHeader>
            <CardTitle>News Articles ({filteredNews.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : filteredNews.length === 0 ? (
              <div className="text-center py-8">
                <Newspaper className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-600">No news articles found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredNews.map(item => (
                  <div
                    key={item.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.status === 'published' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {item.category}
                        </span>
                      </div>
                      {item.summary && (
                        <p className="text-sm text-gray-600 mb-2">{item.summary.substring(0, 150)}...</p>
                      )}
                      <div className="text-xs text-gray-500 space-y-1">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {item.author_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye size={12} />
                            {item.views} views
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(item.id, item.title)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewsManagement;